import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtDT } from "../../lib/constants";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, FileArrowDown } from "@phosphor-icons/react";
import LegalEntitySettingsCard from "./legal/LegalEntitySettingsCard";
import AdminNav from "../../components/AdminNav";

const CORE_FIELDS = [
  { key: "volumetric_divisor", label: "Volumetric divisor", unit: "L×W×H (cm) ÷ divisor = kg", step: "1" },
  { key: "min_billable_weight_kg", label: "Minimum billable weight", unit: "kg", step: "1" },
  { key: "gst_percent", label: "GST", unit: "%", step: "0.01" },
];

// CCC — cargo-insurance commercial parameters. Rate is percentage points (0.05 => 0.05%);
// entered/displayed/saved exactly as-is (NO frontend ×/÷ 100). Backend is authoritative.
// Labels are the approved exact strings; unit hints live in the section hint, not the label.
const INSURANCE_FIELDS = [
  { key: "insurance_flat_threshold", label: "Invoice Value Threshold", step: "1" },
  { key: "insurance_flat_charge", label: "Insurance Amount at or Below Threshold", step: "1" },
  { key: "insurance_rate_percent", label: "Insurance Rate Above Threshold (%)", step: "0.01" },
];

function Section({ title, hint, children, testid }) {
  return (
    <div className="grid-card p-5" data-testid={testid}>
      <div className="overline mb-1">{title}</div>
      {hint && <p className="text-xs text-[hsl(var(--muted))] mb-4">{hint}</p>}
      {children}
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Insurance Operational Closure — SUPER_ADMIN insurance-email configuration. Independent of the
// pricing payload; persists PolicyBazaar recipient list (To) + one Air12 CC address on the shared
// settings row using the existing settings_version optimistic-concurrency token. There is NO
// configurable From address (the real authenticated Gmail SMTP mailbox is always used).
function InsuranceEmailConfigCard({ onVersionSync }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing-settings/insurance-email");
      setCfg(data);
    } catch (e) {
      toast.error(e.displayMessage || "Failed to load insurance-email configuration");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addEmail = () => {
    const v = emailInput.trim();
    if (!v) return;
    if (!EMAIL_RE.test(v)) return toast.error("Enter a valid email address");
    const exists = (cfg.policybazaar_emails || []).some((e) => e.toLowerCase() === v.toLowerCase());
    if (exists) { setEmailInput(""); return; }
    setCfg((c) => ({ ...c, policybazaar_emails: [...(c.policybazaar_emails || []), v] }));
    setEmailInput("");
  };
  const removeEmail = (i) => setCfg((c) => ({
    ...c, policybazaar_emails: c.policybazaar_emails.filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    if (!cfg.policybazaar_emails || cfg.policybazaar_emails.length === 0) {
      return toast.error("Add at least one PolicyBazaar recipient email");
    }
    if (!cfg.cc_email || !EMAIL_RE.test(cfg.cc_email.trim())) {
      return toast.error("Enter a valid Air12 insurance CC email");
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing-settings/insurance-email", {
        policybazaar_emails: cfg.policybazaar_emails,
        cc_email: cfg.cc_email.trim(),
        settings_version: cfg.settings_version,
      });
      setCfg(data);
      // Insurance-email shares the single global settings_version with the main Pricing Settings
      // card. Propagate the server's new version up so the main card's next save isn't a self-stale
      // 409. (Backend optimistic-concurrency guard is unchanged; genuine conflicts still reject.)
      onVersionSync?.(data.settings_version);
      toast.success(`Insurance email configuration saved (v${data.settings_version})`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Settings changed elsewhere — reloading latest. Reapply your changes.");
        load();
      } else {
        toast.error(e.displayMessage || "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <Section title="6 · INSURANCE EMAIL CONFIGURATION" testid="section-insurance-email"
      hint="After MAIN payment, Air12 emails the insurance request to PolicyBazaar (To) with an Air12 copy (CC). The sender address is the Air12 Gmail mailbox and is not configurable here.">
      {loading || !cfg ? (
        <div className="mono text-sm" data-testid="insurance-email-loading">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="overline mb-1.5">PolicyBazaar recipient emails (To)</div>
            <div className="flex gap-2">
              <input className="input-field flex-1" placeholder="name@policybazaar.com"
                value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                data-testid="pb-email-input" />
              <button className="btn-secondary inline-flex items-center gap-1.5" onClick={addEmail} data-testid="pb-email-add">
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="pb-email-chips">
              {(cfg.policybazaar_emails || []).length === 0 ? (
                <span className="text-[11px] text-[hsl(var(--muted))]">No recipients yet — add at least one.</span>
              ) : cfg.policybazaar_emails.map((em, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[hsl(var(--accent)/0.1)] border border-[hsl(var(--accent)/0.3)] text-xs" data-testid={`pb-email-chip-${i}`}>
                  {em}
                  <button className="text-[hsl(var(--danger))]" onClick={() => removeEmail(i)} data-testid={`pb-email-remove-${i}`}><Trash size={12} /></button>
                </span>
              ))}
            </div>
          </div>
          <label className="block max-w-sm">
            <div className="overline mb-1.5">Air12 insurance CC email</div>
            <input className="input-field" placeholder="insurance@air12.in"
              value={cfg.cc_email || ""} onChange={(e) => setCfg((c) => ({ ...c, cc_email: e.target.value }))}
              data-testid="insurance-cc-input" />
          </label>
          <div className="text-[11px] text-[hsl(var(--muted))]" data-testid="insurance-from-readonly">
            Sender (From): <span className="mono">{cfg.from_email || "Air12 Gmail mailbox"}</span> · not configurable
          </div>
          <div className="flex justify-end">
            <button className="btn-primary inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="insurance-email-save-btn">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save insurance email configuration"}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

const MASTER_BANDS = [
  { low: 50, high: 100 }, { low: 100, high: 200 }, { low: 200, high: 350 },
  { low: 350, high: 500 }, { low: 500, high: 750 }, { low: 750, high: 1000 },
  { low: 1000, high: 1250 },
];

// Air Freight — by directional route (independent-save card). `clientScope` (company id) routes
// reads/writes to that Privilege Client's clone; null = Global.
function AirFreightCard({ clientScope }) {
  const [routes, setRoutes] = useState([]);
  const [sel, setSel] = useState("");
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rcBusy, setRcBusy] = useState(false);
  const [rcErr, setRcErr] = useState("");
  const cq = clientScope ? `?client=${encodeURIComponent(clientScope)}` : "";

  const downloadRateCard = async () => {
    if (!sel) return;
    setRcBusy(true); setRcErr("");
    try {
      const url = `/admin/pricing-settings/rate-card?route_code=${encodeURIComponent(sel)}` +
        (clientScope ? `&client=${encodeURIComponent(clientScope)}` : "");
      const res = await api.get(url, { responseType: "blob" });
      const href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = href; a.download = `Air12_RateCard_${sel}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(href);
      toast.success(`Rate Card generated for ${sel}`);
    } catch (e) {
      let msg = "Couldn\u2019t generate rate card. Retry.";
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) { const j = JSON.parse(txt); msg = j?.detail?.message || j?.message || msg; }
      } catch { /* keep default */ }
      setRcErr(msg); toast.error(msg);
    } finally { setRcBusy(false); }
  };

  const loadList = async () => {
    try {
      const { data } = await api.get("/admin/pricing-settings/air-freight");
      setRoutes(data.routes || []);
    } catch (e) { toast.error(e.displayMessage || "Failed to load routes"); }
  };
  useEffect(() => { loadList(); }, []);
  // Reset the open route when the pricing context switches.
  useEffect(() => { setSel(""); setCfg(null); }, [clientScope]);

  const loadRoute = async (rc) => {
    setSel(rc); setCfg(null); setRcErr("");
    if (!rc) return;
    try {
      const { data } = await api.get(`/admin/pricing-settings/air-freight/${rc}${cq}`);
      const bands = (data.bands && data.bands.length === 7)
        ? data.bands.map((b, i) => ({ upto_kg: MASTER_BANDS[i].high, rate: b.rate ?? "" }))
        : MASTER_BANDS.map((b) => ({ upto_kg: b.high, rate: "" }));
      setCfg({ ...data, bands });
    } catch (e) { toast.error(e.displayMessage || "Failed to load route pricing"); }
  };

  const setRate = (i, v) => setCfg((c) => ({ ...c, bands: c.bands.map((b, idx) => idx === i ? { ...b, rate: v } : b) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        config_revision: cfg.config_revision,
        bands: cfg.bands.map((b) => ({ upto_kg: b.upto_kg, rate: (b.rate === "" || b.rate == null ? null : Number(b.rate)) })),
      };
      const { data } = await api.put(`/admin/pricing-settings/air-freight/${sel}${cq}`, payload);
      const bands = data.bands.map((b, i) => ({ upto_kg: MASTER_BANDS[i].high, rate: b.rate ?? "" }));
      setCfg({ ...data, bands });
      toast.success(`Air Freight saved for ${sel}`);
      loadList();
    } catch (e) {
      if (e?.response?.status === 409) { toast.error("Config changed elsewhere — reloading."); loadRoute(sel); }
      else if (Array.isArray(e?.response?.data?.detail?.errors)) toast.error(e.response.data.detail.errors.join("; "));
      else toast.error(e.displayMessage || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Section title="2 · AIR FREIGHT — BY DIRECTIONAL ROUTE" testid="section-air-freight"
      hint="Route-specific progressive ₹/kg across seven fixed weight bands. Explicit ₹0 is valid. A route with incomplete rates is not auto-priceable (no global fallback). Above 1,250 kg, additional weight is charged at the existing 1,000–1,250 kg rate.">
      <label className="block mb-4 max-w-sm">
        <div className="overline mb-1.5">Route</div>
        <select className="input-field" value={sel} onChange={(e) => loadRoute(e.target.value)} data-testid="air-freight-route-select">
          <option value="">Select a route…</option>
          {routes.map((r) => (
            <option key={r.route_code} value={r.route_code}>
              {r.route_label} {r.air_freight_configured ? "✓ priced" : "• not priced"}{r.active_for_booking ? "" : " · inactive"}
            </option>
          ))}
        </select>
      </label>
      {cfg && (
        <div className="space-y-3" data-testid="air-freight-editor">
          <div className="text-sm text-[hsl(var(--muted))]">
            {cfg.source_airport_code} → {cfg.destination_airport_code}
            {!cfg.active_for_booking && <span className="ml-2 text-xs">(not active for booking — pricing may still be configured)</span>}
          </div>
          {cfg.bands.map((b, i) => (
            <div key={i} className="flex items-end gap-3" data-testid={`af-band-${i}`}>
              <div className="flex-1 overline pb-2">{MASTER_BANDS[i].low}–{MASTER_BANDS[i].high} kg</div>
              <label className="flex-1">
                <div className="overline mb-1.5">Rate (₹/kg)</div>
                <input type="number" min="0" step="0.01" className="input-field"
                  value={b.rate ?? ""} onChange={(e) => setRate(i, e.target.value)} data-testid={`af-band-${i}-rate`} />
              </label>
            </div>
          ))}
          <div className="flex justify-end">
            <button className="btn-primary inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="air-freight-save-btn">
              <FloppyDisk size={16} /> {saving ? "Saving…" : `Save Air Freight (${sel})`}
            </button>
          </div>
          <div className="flex flex-col items-end gap-1.5 pt-3 mt-2 border-t border-[hsl(var(--border))]" data-testid="rate-card-block">
            <button className="btn-secondary inline-flex items-center gap-2" disabled={rcBusy || !sel}
              onClick={downloadRateCard} data-testid="rate-card-download-btn">
              <FileArrowDown size={16} /> {rcBusy ? "Generating…" : "Download Rate Card"}
            </button>
            {rcErr && <div className="text-xs text-red-600" data-testid="rate-card-error">{rcErr}</div>}
            <div className="text-[11px] text-[hsl(var(--muted))] text-right">
              Client PDF for {sel} using current {clientScope ? "Privilege" : "Global"} pricing · pre-GST · generated fresh each download.
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// Helper (Load Assist) seven-band card (independent-save). `clientScope` routes to a Privilege
// Client's clone; null = Global.
function HelperCard({ clientScope }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const cq = clientScope ? `?client=${encodeURIComponent(clientScope)}` : "";

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/load-assist/settings${cq}`);
      const bands = (data.helper_bands && data.helper_bands.length === 7)
        ? data.helper_bands.map((b, i) => ({ upto_kg: MASTER_BANDS[i].high, source_amount: b.source_amount ?? "", delivery_amount: b.delivery_amount ?? "" }))
        : MASTER_BANDS.map((b) => ({ upto_kg: b.high, source_amount: "", delivery_amount: "" }));
      setS({ ...data, helper_bands: bands });
    } catch (e) { toast.error(e.displayMessage || "Failed to load Helper settings"); }
  };
  useEffect(() => { load(); }, [clientScope]);

  const setAmt = (i, k, v) => setS((c) => ({ ...c, helper_bands: c.helper_bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        la_config_revision: s.la_config_revision,
        enabled: s.enabled,
        helper_bands: s.helper_bands.map((b) => ({ upto_kg: b.upto_kg, source_amount: (b.source_amount === "" || b.source_amount == null ? null : Number(b.source_amount)), delivery_amount: (b.delivery_amount === "" || b.delivery_amount == null ? null : Number(b.delivery_amount)) })),
      };
      const { data } = await api.put(`/admin/load-assist/settings${cq}`, payload);
      const bands = data.helper_bands.map((b, i) => ({ upto_kg: MASTER_BANDS[i].high, source_amount: b.source_amount ?? "", delivery_amount: b.delivery_amount ?? "" }));
      setS({ ...data, helper_bands: bands });
      toast.success("Helper settings saved");
    } catch (e) {
      if (e?.response?.status === 409) { toast.error("Settings changed elsewhere — reloading."); load(); }
      else if (Array.isArray(e?.response?.data?.detail?.errors)) toast.error(e.response.data.detail.errors.join("; "));
      else toast.error(e.displayMessage || "Save failed");
    } finally { setSaving(false); }
  };

  if (!s) return null;
  const helperTitle = clientScope ? "3b · HELPER (CLIENT)" : "3b · HELPER (GLOBAL)";
  return (
    <Section title={helperTitle} testid="section-helper"
      hint="Priced from total actual gross weight (min 50 kg) across seven fixed bands. Source/Pickup and Destination/Delivery amounts are independent; explicit ₹0 is valid. Applies to all routes. Above 1,250 kg actual gross weight, each complete 1,250 kg block uses the 1,000–1,250 kg band, with remaining weight charged using its applicable existing band.">
      <label className="flex items-center gap-2 mb-4 text-sm">
        <input type="checkbox" checked={!!s.enabled} onChange={(e) => setS((c) => ({ ...c, enabled: e.target.checked }))} data-testid="helper-enabled-toggle" />
        Enable Helper (Load Assist)
      </label>
      <div className="space-y-3">
        {s.helper_bands.map((b, i) => (
          <div key={i} className="flex items-end gap-3" data-testid={`helper-band-${i}`}>
            <div className="flex-1 overline pb-2">{MASTER_BANDS[i].low}–{MASTER_BANDS[i].high} kg</div>
            <label className="flex-1">
              <div className="overline mb-1.5">Source / Pickup (₹)</div>
              <input type="number" min="0" step="1" className="input-field"
                value={b.source_amount ?? ""} onChange={(e) => setAmt(i, "source_amount", e.target.value)} data-testid={`helper-band-${i}-source`} />
            </label>
            <label className="flex-1">
              <div className="overline mb-1.5">Destination / Delivery (₹)</div>
              <input type="number" min="0" step="1" className="input-field"
                value={b.delivery_amount ?? ""} onChange={(e) => setAmt(i, "delivery_amount", e.target.value)} data-testid={`helper-band-${i}-delivery`} />
            </label>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <button className="btn-primary inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="helper-save-btn">
          <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Helper settings"}
        </button>
      </div>
    </Section>
  );
}


// Privilege Client scope selector + Add/Remove. SUPER_ADMIN only. Never creates a company.
function PricingScopeSelector({ clientScope, setClientScope, clients, setClients, reloadClients }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (term) => {
    setQ(term);
    try {
      const { data } = await api.get(`/admin/pricing-settings/eligible-companies?q=${encodeURIComponent(term)}`);
      setResults(data.companies || []);
    } catch (e) { toast.error(e.displayMessage || "Search failed"); }
  };

  const add = async (company_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pricing-settings/privilege-clients", { company_id });
      toast.success(`Privilege pricing created for ${data.company_name || data.public_company_id}`);
      setAdding(false); setQ(""); setResults([]);
      await reloadClients();
      setClientScope(data.company_id);
    } catch (e) {
      if (e?.response?.status === 409) toast.error("This company is already a Privilege Client.");
      else toast.error(e.displayMessage || "Could not add Privilege Client. Global pricing still applies.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    const cur = clients.find((c) => c.company_id === clientScope);
    const name = cur ? (cur.company_name || cur.public_company_id) : "this client";
    if (!window.confirm(
      `Remove Privilege pricing for ${name}?\n\nFuture quotes will immediately use GLOBAL pricing. Already accepted/frozen quotes are unaffected. This is not a deletion — the pricing becomes inactive history.`
    )) return;
    setBusy(true);
    try {
      await api.delete(`/admin/pricing-settings/privilege-clients/${clientScope}`);
      toast.success("Privilege Client removed — future quotes use Global pricing.");
      // Optimistic in-place update: drop from the list + reset scope immediately (so the selector,
      // banner and Global-only sections revert without a page reload), then reconcile with server.
      const removedId = clientScope;
      setClients((prev) => prev.filter((c) => c.company_id !== removedId));
      setClientScope(null);
      await reloadClients();
    } catch (e) { toast.error(e.displayMessage || "Remove failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid-card p-4 mb-6" data-testid="pricing-scope-selector">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="overline">Pricing for:</span>
        <select className="input-field max-w-xs" value={clientScope || ""} data-testid="pricing-scope-select"
          onChange={(e) => setClientScope(e.target.value || null)}>
          <option value="">Global Pricing</option>
          {clients.map((c) => (
            <option key={c.company_id} value={c.company_id}>
              {c.company_name || c.public_company_id} ({c.public_company_id})
            </option>
          ))}
        </select>
        <button className="btn-secondary inline-flex items-center gap-1.5" onClick={() => { setAdding(true); search(""); }}
          data-testid="add-privilege-client-btn"><Plus size={14} /> Add Privilege Client</button>
        {clientScope && (
          <button className="btn-ghost inline-flex items-center gap-1.5 text-[hsl(var(--danger))]" onClick={remove}
            disabled={busy} data-testid="remove-privilege-client-btn"><Trash size={14} /> Remove Privilege Client</button>
        )}
      </div>
      {clientScope && (
        <p className="text-sm mt-3 px-3 py-2 rounded bg-[hsl(var(--accent)/0.1)] border border-[hsl(var(--accent)/0.3)]"
          data-testid="pricing-scope-banner">
          Editing pricing for <span className="mono font-semibold">
            {(clients.find((c) => c.company_id === clientScope) || {}).public_company_id || clientScope}</span>
          {" — "}{(clients.find((c) => c.company_id === clientScope) || {}).company_name || ""}.
          Changes apply to this client's future quotes only. Insurance email routing stays Global.
        </p>
      )}

      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="add-client-modal"
          onClick={() => setAdding(false)}>
          <div className="grid-card bg-[hsl(var(--bg))] p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">Add Privilege Client</div>
            <p className="text-xs text-[hsl(var(--muted))] mb-3">
              Search an EXISTING Sender Company by name, SCO id, or a member's email. Adding clones the
              current Global pricing into an editable client set. This never creates a company.
            </p>
            <input className="input-field mb-3" autoFocus placeholder="Search name / SCO-000123 / email"
              value={q} onChange={(e) => search(e.target.value)} data-testid="add-client-search-input" />
            <div className="max-h-64 overflow-auto space-y-1" data-testid="add-client-results">
              {results.length === 0 ? (
                <div className="text-xs text-[hsl(var(--muted))] py-2">No eligible companies found.</div>
              ) : results.map((c) => (
                <button key={c.company_id} disabled={busy}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--border))]"
                  onClick={() => add(c.company_id)} data-testid={`add-client-option-${c.public_company_id}`}>
                  <div className="text-sm font-medium">{c.company_name}</div>
                  <div className="mono text-[11px] text-[hsl(var(--muted))]">{c.public_company_id} · {c.status}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-ghost" onClick={() => setAdding(false)} data-testid="add-client-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function PricingSettings() {
  const { profile } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [errors, setErrors] = useState([]);
  const [gstError, setGstError] = useState("");
  const [stale, setStale] = useState(false);
  const [clientScope, setClientScope] = useState(null);
  const [clients, setClients] = useState([]);
  // Derive the ACTIVE scope from the authoritative clients list: if the selected company is no longer
  // an active Privilege Client (e.g. just removed), scope collapses to Global immediately regardless
  // of state-update timing. This drives settings load, cq, gating, and the selector consistently.
  const effectiveScope = clients.some((c) => c.company_id === clientScope) ? clientScope : null;
  const cq = effectiveScope ? `?client=${encodeURIComponent(effectiveScope)}` : "";

  const reloadClients = async () => {
    try {
      const { data } = await api.get("/admin/pricing-settings/privilege-clients");
      setClients(data.clients || []);
    } catch (e) { /* selector still usable with Global */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/pricing-settings/settings${cq}`);
      setSettings(data);
      setStale(false);
      setErrors([]);
    } catch (e) {
      toast.error(e.displayMessage || "Failed to load pricing settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadClients(); }, []);
  useEffect(() => { load(); }, [effectiveScope]);

  if (profile && profile.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;

  const setField = (k, v) => {
    if (k === "gst_percent") setGstError("");
    setSettings((s) => ({ ...s, [k]: v }));
  };
  const setBand = (i, k, v) => setSettings((s) => ({
    ...s, local_movement_bands: s.local_movement_bands.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)),
  }));
  const setMarkupBand = (i, v) => setSettings((s) => ({
    ...s, core_custody_fee_bands: (s.core_custody_fee_bands || []).map((r, idx) => (idx === i ? { ...r, amount: v } : r)),
  }));

  const num = (v) => (v === "" || v == null ? null : Number(v));

  // Independent per-section save. Submits ONLY that section's fields to its dedicated endpoint and,
  // on success, syncs ONLY the shared settings_version — never replacing sibling sections' unsaved
  // form edits. Loading/error/stale/toast behaviour matches the rest of the page.
  const saveSection = async (name, suffix, fields) => {
    setSavingSection(name);
    setErrors([]);
    try {
      const { data } = await api.put(`/admin/pricing-settings/settings/${suffix}${cq}`, {
        ...fields, settings_version: settings.settings_version,
      });
      setSettings((s) => (s ? { ...s, settings_version: data.settings_version } : s));
      setStale(false);
      toast.success(`Saved (v${data.settings_version})`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        setStale(true);
        toast.error("Settings were changed elsewhere. Your edits are kept — reload the latest and reapply.");
      } else if (Array.isArray(detail?.errors)) {
        setErrors(detail.errors);
        toast.error("Please fix the highlighted validation errors.");
      } else {
        toast.error(e.displayMessage || "Save failed");
      }
    } finally {
      setSavingSection(null);
    }
  };

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[960px] mx-auto p-6" data-testid="pricing-settings-page">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="overline">CONFIGURATION</div>
          <h1 className="heading text-3xl">Pricing Settings</h1>
        </div>
      </div>

      <div className="grid-card p-4 mb-6 border-l-2 border-[hsl(var(--accent))]">
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="new-bookings-only-note">
          Changes apply only to new estimates, quotes and bookings. Existing booked shipments retain
          their captured pricing snapshot.
          {settings && (
            <span className="mono ml-2" data-testid="pricing-settings-version">v{settings.settings_version}</span>
          )}
        </p>
        {settings && (settings.updated_at || settings.updated_by_email) && (
          <p className="text-[11px] text-[hsl(var(--muted))] mt-1" data-testid="pricing-settings-history">
            Last updated {settings.updated_at ? fmtDT(settings.updated_at) : "—"}
            {settings.updated_by_email ? ` by ${settings.updated_by_email}` : ""}
          </p>
        )}
      </div>

      {stale && (
        <div className="grid-card p-4 mb-4 border-l-2 border-red-500" data-testid="pricing-stale-banner">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm">
              These settings were changed elsewhere. Your unsaved edits are still here. Reload the
              latest configuration, then reapply your changes.
            </p>
            <button className="btn-ghost shrink-0" onClick={load} data-testid="pricing-reload-btn">Reload latest</button>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="grid-card p-4 mb-4 border-l-2 border-red-500" data-testid="pricing-errors">
          <ul className="text-sm text-red-500 list-disc pl-5 space-y-1">
            {errors.map((er, i) => <li key={i}>{er}</li>)}
          </ul>
        </div>
      )}

      <PricingScopeSelector clientScope={effectiveScope} setClientScope={setClientScope}
        clients={clients} setClients={setClients} reloadClients={reloadClients} />

      {/* Scope-keyed region so switching Global<->client fully remounts the pricing controls and
          the Global-only cards (insurance-email / legal) mount fresh without a page reload. */}
      <div key={effectiveScope || "global"}>
      {/* M2 Slice 9 — Legal Entity Settings (GLOBAL/operational only; hidden in a client scope). */}
      {!effectiveScope && (
        <div className="mb-6">
          <LegalEntitySettingsCard />
        </div>
      )}

      {loading || !settings ? (
        <div className="mono text-sm" data-testid="pricing-loading">Loading…</div>
      ) : (
        <div className="space-y-6">
          {/* 1. Core Pricing */}
          <Section title="1 · CORE PRICING" testid="section-core"
            hint="Divisor: chargeable weight = volumetric vs actual, whichever is higher, rounded up to 0.5 kg.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CORE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <div className="overline mb-1.5">{f.label} <span className="text-[hsl(var(--muted))] normal-case">({f.unit})</span></div>
                  <input type="number" min={f.key === "gst_percent" ? "0.01" : f.key === "min_billable_weight_kg" ? "1" : "0"} step={f.step} className="input-field"
                    value={settings[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`pricing-${f.key}`} />
                  {f.key === "gst_percent" && gstError && (
                    <div className="text-xs text-[hsl(var(--danger))] mt-1.5" data-testid="pricing-gst_percent-error">{gstError}</div>
                  )}
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-primary inline-flex items-center gap-2" disabled={savingSection === "core"}
                onClick={() => {
                  // To-Do 2: configured GST must be > 0% (Global and Privilege alike). Backend is authoritative.
                  if (!(Number(settings.gst_percent) > 0)) {
                    setGstError("GST must be greater than 0%.");
                    return;
                  }
                  setGstError("");
                  saveSection("core", "core", {
                    volumetric_divisor: Number(settings.volumetric_divisor),
                    min_billable_weight_kg: Number(settings.min_billable_weight_kg),
                    gst_percent: Number(settings.gst_percent),
                  });
                }} data-testid="core-save-btn">
                <FloppyDisk size={16} /> {savingSection === "core" ? "Saving…" : "Save Core Pricing"}
              </button>
            </div>
          </Section>

          {/* 1b. Slab-wise Air12 markup — seven fixed master bands (replaces the single fee) */}
          <Section title="1b · AIR12 FEE — SLAB-WISE MARKUP" testid="section-core-markup"
            hint="Air12 fee per weight slab, using the seven fixed master weight bands. Set an independent ₹ markup for each slab; explicit ₹0 is valid; every slab must be filled to activate pricing. Above 1,250 kg, the existing 1,000–1,250 kg Core Custody fee continues to apply once.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(settings.core_custody_fee_bands || []).map((band, i) => (
                <label key={i} className="block" data-testid={`markup-band-${i}`}>
                  <div className="overline mb-1.5">
                    Band {i + 1} · {MASTER_BANDS[i].low}–{MASTER_BANDS[i].high} kg <span className="text-[hsl(var(--muted))] normal-case">(₹)</span>
                  </div>
                  <input type="number" min="0" step="1" className="input-field"
                    value={band.amount ?? ""} onChange={(e) => setMarkupBand(i, e.target.value)}
                    data-testid={`markup-band-${i}-amount`} />
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-primary inline-flex items-center gap-2" disabled={savingSection === "markup"}
                onClick={() => saveSection("markup", "markup", {
                  core_custody_fee_bands: (settings.core_custody_fee_bands || []).map((b) => ({
                    upto_kg: Number(b.upto_kg), amount: num(b.amount),
                  })),
                })} data-testid="markup-save-btn">
                <FloppyDisk size={16} /> {savingSection === "markup" ? "Saving…" : "Save Air12 Fee"}
              </button>
            </div>
          </Section>

          {/* 2. Air Freight — by directional route */}
          <AirFreightCard clientScope={effectiveScope} />

          {/* 3. Local Movement Charges — global, seven fixed master bands */}
          <Section title="3 · LOCAL MOVEMENT CHARGES (GLOBAL)" testid="section-local"
            hint="Per side, by zone (A ≤25km, B ≤50km, C ≤80km). Seven fixed weight bands. Explicit ₹0 is valid; every cell must be filled to activate. Above 1,250 kg, each complete 1,250 kg block uses the 1,000–1,250 kg band, with remaining weight charged using its applicable existing band.">
            <div className="space-y-3">
              {(settings.local_movement_bands || []).map((band, i) => (
                <div key={i} className="border border-[hsl(var(--border))] rounded p-3" data-testid={`lm-band-${i}`}>
                  <div className="overline mb-2">Band {i + 1} · {band.low_kg}–{band.high_kg} kg</div>
                  <div className="grid grid-cols-3 gap-3">
                    <label><div className="overline mb-1.5">Zone A (₹)</div>
                      <input type="number" min="0" step="1" className="input-field"
                        value={band.zone_a ?? ""} onChange={(e) => setBand(i, "zone_a", e.target.value)}
                        data-testid={`lm-band-${i}-zone-a`} /></label>
                    <label><div className="overline mb-1.5">Zone B (₹)</div>
                      <input type="number" min="0" step="1" className="input-field"
                        value={band.zone_b ?? ""} onChange={(e) => setBand(i, "zone_b", e.target.value)}
                        data-testid={`lm-band-${i}-zone-b`} /></label>
                    <label><div className="overline mb-1.5">Zone C (₹)</div>
                      <input type="number" min="0" step="1" className="input-field"
                        value={band.zone_c ?? ""} onChange={(e) => setBand(i, "zone_c", e.target.value)}
                        data-testid={`lm-band-${i}-zone-c`} /></label>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-primary inline-flex items-center gap-2" disabled={savingSection === "local"}
                onClick={() => saveSection("local", "local-movement", {
                  local_movement_bands: (settings.local_movement_bands || []).map((b) => ({
                    low_kg: Number(b.low_kg), high_kg: Number(b.high_kg),
                    zone_a: num(b.zone_a), zone_b: num(b.zone_b), zone_c: num(b.zone_c),
                  })),
                })} data-testid="local-movement-save-btn">
                <FloppyDisk size={16} /> {savingSection === "local" ? "Saving…" : "Save Local Movement"}
              </button>
            </div>
          </Section>

          {/* 3b. Helper — seven fixed master bands */}
          <HelperCard clientScope={effectiveScope} />
          {/* 4. Insurance Pricing */}
          <Section title="4 · INSURANCE PRICING" testid="section-insurance"
            hint="Cargo insurance (pre-GST, folded into the quote). At or below the threshold a flat amount applies; above it, the rate is a percentage of the full declared invoice value. Rate is in percent (e.g. 0.05 = 0.05%). Captured into each new quote snapshot.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {INSURANCE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <div className="overline mb-1.5">{f.label}</div>
                  <input type="number" min="0" step={f.step} className="input-field"
                    value={settings[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`pricing-${f.key}`} />
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-primary inline-flex items-center gap-2" disabled={savingSection === "insurance"}
                onClick={() => saveSection("insurance", "insurance", {
                  insurance_flat_threshold: Number(settings.insurance_flat_threshold),
                  insurance_flat_charge: Number(settings.insurance_flat_charge),
                  insurance_rate_percent: Number(settings.insurance_rate_percent),
                })} data-testid="insurance-pricing-save-btn">
                <FloppyDisk size={16} /> {savingSection === "insurance" ? "Saving…" : "Save Insurance Pricing"}
              </button>
            </div>
          </Section>

          {!effectiveScope && <InsuranceEmailConfigCard onVersionSync={(v) =>
            setSettings((s) => (s && v != null ? { ...s, settings_version: v } : s))} />}

          {/* 5b. Concierge Service — flat taxable pre-booking assistance fee (Global + Client scope) */}
          <Section title="5b · CONCIERGE SERVICE" testid="section-concierge"
            hint="Flat Air12 Concierge pre-booking assistance fee. Taxable: it is added once into the quote subtotal so the same GST applies to the combined sum (no separate Concierge GST). Explicit ₹0 is valid; it always appears as its own line on finance documents. Applies to Concierge conversions on this scope.">
            <label className="block max-w-xs">
              <div className="overline mb-1.5">Concierge Service fee <span className="text-[hsl(var(--muted))] normal-case">(₹)</span></div>
              <input type="number" min="0" step="1" className="input-field"
                value={settings.concierge_service_fee ?? ""} onChange={(e) => setField("concierge_service_fee", e.target.value)}
                data-testid="pricing-concierge_service_fee" />
            </label>
            <div className="flex justify-end mt-4">
              <button className="btn-primary inline-flex items-center gap-2" disabled={savingSection === "concierge"}
                onClick={() => saveSection("concierge", "concierge", {
                  concierge_service_fee: Number(settings.concierge_service_fee),
                })} data-testid="concierge-save-btn">
                <FloppyDisk size={16} /> {savingSection === "concierge" ? "Saving…" : "Save Concierge Service"}
              </button>
            </div>
          </Section>
        </div>
      )}
      </div>
    </div>
    </>
  );
}
